import * as React from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { clearItemsInBasket, deleteItemInBasket, fetchBasket, updateItemInBasket } from '@/services';
import type { BasketItem, BasketResponse, SystemQuote } from '@/types';
import { Button } from '@/components/atoms/button.tsx';
import { ViewSystem } from '@/components/molecules/ViewSystem.tsx';
import { LoadingScreen } from '@/components/molecules/LoadingScreen.tsx';
import { queryOptions, useMutation, useQueryClient, useSuspenseQuery } from '@tanstack/react-query';
import { BASKET_STALE_TIME_5_MINS } from '@/config';
import { ErrorComponent } from '@/components/molecules/ErrorComponent';
import { BasketItemsList } from '@/components/molecules/BasketItemsList';
import { EmptyBasket } from '@/components/molecules/EmptyBasket';
import { ConfirmationAlert } from '@/components/molecules/ConfirmationAlert';

const BASKET_QUERY_KEY = ['basket'];

const basketQueryOptions = queryOptions({
  queryKey: BASKET_QUERY_KEY,
  queryFn: fetchBasket,
  staleTime: BASKET_STALE_TIME_5_MINS
});

export const Route = createFileRoute('/basket')({
  loader: async({ context }) => {
    return context.queryClient?.ensureQueryData(basketQueryOptions)
  },
  preload: true,
  shouldReload: true,
  component: Basket,
  pendingComponent: LoadingScreen
});

export type BasketItemsListProps = {
  items: BasketItem[]
  onDelete: (item: BasketItem) => () => void
  onQtyChange: (item: BasketItem) => (value: number) => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  handleViewSystem: (item: any) => () => void
}




function Basket() {
  const {  error, data } = useSuspenseQuery(basketQueryOptions);
  const queryClient = useQueryClient();
  const [loadingMessage, setLoadingMessage] = React.useState('Clearing basket...');

  const [confirmAlertIsOpen, setConfirmAlertIsOpen] = React.useState(false);
  const [confirmMessage, setConfirmMessage] = React.useState('');
  const [confirmAction, setConfirmAction] = React.useState('REMOVE_ITEM');
  const [basketItem, setBasketItem] = React.useState<BasketItem | null>(null);

  const onSuccess = (data: BasketResponse) => {
    queryClient.setQueryData(BASKET_QUERY_KEY, data);
  }

  const mutationDeleteItem = useMutation({
    mutationFn: (basketItem: BasketItem | null) => {
      return deleteItemInBasket(basketItem);
    },
    onSuccess,
  });

  const mutationClearBasket = useMutation({
    mutationFn: clearItemsInBasket,
    onSuccess,
  });

  const mutationUpdateBasketItem = useMutation({
    mutationFn: (updatedItem: BasketItem) => {
     return updateItemInBasket(updatedItem.qty, updatedItem._id);
    },
    onSuccess,
  });

  const [system, setSystem] = React.useState<SystemQuote | null>(null);
  const [isOpen, setIsOpen] = React.useState(false);

  const handleDeleteItem = (basketItem: BasketItem) => () => {
    setBasketItem(basketItem);
    setConfirmAlertIsOpen(true);
    setConfirmMessage(`Are you sure you want to remove the item: ${basketItem.item.name} from your basket?`);
    setConfirmAction('REMOVE_ITEM');
    setLoadingMessage('Removing item...');
  }

  const handleQtyChange = (basketItem: BasketItem) => (value: number) => {
    mutationUpdateBasketItem.mutate({
      ...basketItem,
      qty: value,
    });
  }

  const handleViewSystem = (system: SystemQuote) => () => {
    setSystem(system);
    setIsOpen(true);
  }

  const handleClearBasket= () => {
    setConfirmAlertIsOpen(true);
    setConfirmMessage('Are you sure you want to clear your basket?');
    setConfirmAction('CLEAR_BASKET');
    setLoadingMessage('Clearing basket...');
  }

  const handleCheckout = () => {

  }

  const handleConfirmAlert = () => {
    if (confirmAction === 'REMOVE_ITEM') {
      mutationDeleteItem.mutate(basketItem);
    } else if (confirmAction === 'CLEAR_BASKET') {
      mutationClearBasket.mutate();
    }
  }

  if(error) {
    return(<ErrorComponent error={error}/>)
  }
  if(data) {
    const { basket } = data;
    if (basket.items.length) {
      return (<div className="main-content mb-16">
        <ConfirmationAlert
          isOpen={confirmAlertIsOpen}
          onOpenChange={setConfirmAlertIsOpen}
          message={confirmMessage}
          loadingMessage="Clearing basket..."
          onConfirm={handleConfirmAlert}
        />
        <div className="flex flex-row justify-between">
          <div className="basis-2/3">
            <h1 className="text-3xl font-bold">My Basket</h1>
          </div>
          <div className="basis-1/3 text-right">
            <Button variant="secondary" onClick={handleClearBasket}>Clear Basket</Button>
          </div>
        </div>
        <BasketItemsList total={basket.total} handleViewSystem={handleViewSystem} onQtyChange={handleQtyChange} items={basket.items} onDelete={handleDeleteItem}/>
        <div className="space-x-4 mt-4">
          <Button className="w-full" onClick={handleCheckout}>Checkout</Button>
        </div>
        <ViewSystem isOpen={isOpen} onOpenChange={setIsOpen} system={system}/>
      </div>);
    }
    return(<EmptyBasket />);
  }
  return(<div className="main-content"><h3>Something went wrong</h3></div>);
}
